import React, { Component } from 'react'
import { Alert, Button, Card, Checkbox, Empty, Modal, Popover, Radio, Select, Space, Spin, Steps, Table, Tag, Tooltip, Typography, message } from 'antd'
import {
    CheckCircleOutlined,
    CopyOutlined,
    DeleteOutlined,
    EditOutlined,
    EyeOutlined,
    ReadOutlined,
    ReloadOutlined,
    SafetyCertificateOutlined,
    UserAddOutlined,
} from '@ant-design/icons'
import {
    deleteVibeOwnerPagePermission,
    deleteVibePageAccessRoute,
    getDingdingDeptList,
    getUserListForDropbox,
    getVibeOwnerAccessContext,
    getVibeOwnerAccessProjects,
    getVibeOwnerAccessStatus,
    requestVibeOwnerPageAccess,
    replaceVibeOwnerPagePermission,
} from '../../request'
import { buildDepartmentOptions, buildUserOptions } from './vibeOwnerAccessOptions'
import VibeRouteList from '../vibe/components/VibeRouteList'
import VibeOwnerAccessPermissionPanel from './VibeOwnerAccessPermissionPanel'
import {
    buildApplicationEntryUrl,
    buildOwnerDirectEntryUrl,
    buildSafePagePreviewUrl,
    resolveSafeReturnPage,
} from './vibeOwnerAccessNavigation'
import { getDeniedReasonAction } from './vibeOwnerAccessReason'
import {
    buildAccessRequestText,
    buildCurrentPageFromParams,
    buildPageAccessList,
    getOwnerSetupState,
    getPageDisplay,
    matchRouteByPage,
    resolveCurrentPage,
    summarizeGrantedPages,
} from './vibeOwnerAccessStatus'
import './index.less'

const { Title, Text, Paragraph } = Typography

const formatTime = value => {
    if (!value) return '-'
    const date = new Date(value)
    if (Number.isNaN(date.getTime())) return String(value)
    const pad = number => String(number).padStart(2, '0')
    return `${date.getFullYear()}-${pad(date.getMonth() + 1)}-${pad(date.getDate())} ${pad(date.getHours())}:${pad(date.getMinutes())}`
}

export default class VibeOwnerAccess extends Component {
    state = {
        loading: true, error: '', projects: [], projectId: undefined, context: null, statusContext: null,
        selectedRouteIds: [], accessMode: undefined, subjectIds: [],
        includeChildren: true, users: [], departments: [], saving: false,
        optionLoading: false,
        drawerOpen: false, drawerMode: 'create', editingPermission: null, deletingKey: null,
        accessRequestSubmitting: false, accessRequested: false,
        currentPage: null, focusedRouteId: null,
        routeSyncExpanded: false,
        deletingRouteId: null,
    }

    componentDidMount() {
        if (this.props.embedded && this.props.projectId) {
            this.requestedProjectId = String(this.props.projectId)
            this.setState({ projectId: this.props.projectId }, () => this.loadContext(this.props.projectId))
            return
        }
        const params = new URLSearchParams(this.props.location?.search || '')
        this.requestedProjectId = params.get('projectId')
        this.returnPage = params.get('returnPage') || ''
        this.currentPage = this.buildCurrentPage(params)
        this.setState({ currentPage: this.currentPage })
        this.deniedReason = params.get('reason') || ''
        this.statusView = params.get('view') === 'status'
            || this.props.location?.pathname === '/portal/vibe-access/denied'
        if (this.statusView) {
            this.loadStatusContext()
            return
        }
        this.loadProjects()
    }

    componentDidUpdate(prevProps) {
        if (this.props.embedded
            && String(prevProps.projectId || '') !== String(this.props.projectId || '')
            && this.props.projectId) {
            this.requestedProjectId = String(this.props.projectId)
            this.setState({ projectId: this.props.projectId }, () => this.loadContext(this.props.projectId))
        }
    }

    loadStatusContext = async () => {
        if (!/^\d+$/.test(this.requestedProjectId || '')) {
            this.setState({ loading: false, error: '缺少应用信息。请从被拦截的页面进入，或打开应用权限设置页。' })
            return
        }
        this.setState({ loading: true, error: '' })
        try {
            const statusContext = await getVibeOwnerAccessStatus({
                projectId: this.requestedProjectId,
                returnPage: this.returnPage,
            })
            this.setState({ loading: false, statusContext })
        } catch (error) {
            this.setState({ loading: false, error: error?.message || '应用状态加载失败，请稍后重试' })
        }
    }

    copyAccessRequest = async () => {
        try {
            const currentPage = resolveCurrentPage(this.state.currentPage, this.state.statusContext)
            await navigator.clipboard.writeText(buildAccessRequestText({
                projectName: this.state.statusContext?.projectName,
                ownerUserNick: this.state.statusContext?.ownerUserNick,
                pageName: currentPage?.name,
                pagePath: currentPage?.path,
            }))
            message.success('申请信息已复制')
        } catch (error) {
            message.error('复制失败，请检查浏览器剪贴板权限')
        }
    }

    buildCurrentPage = params => buildCurrentPageFromParams(params)

    getEntryUrl = () => this.state.context?.entryUrl || this.state.statusContext?.entryUrl || ''

    openPagePreview = page => {
        const display = getPageDisplay(page || {})
        const entryUrl = this.getEntryUrl()
        const safePreviewUrl = (page?.previewUrl ? resolveSafeReturnPage(page.previewUrl, entryUrl) : '')
            || (page?.url ? resolveSafeReturnPage(page.url, entryUrl) : '')
            || buildSafePagePreviewUrl(entryUrl, page?.pathPattern || page?.path || page?.url || display.path)
        const previewUrl = this.statusView ? safePreviewUrl : buildOwnerDirectEntryUrl(safePreviewUrl)
        if (!previewUrl) {
            message.warning('暂时无法预览这个页面')
            return
        }
        window.open(previewUrl, '_blank', 'noopener,noreferrer')
    }

    previewOwnerPage = (routes = []) => {
        const focused = routes.find(route => String(route.id) === String(this.state.focusedRouteId))
            || matchRouteByPage(routes, this.state.currentPage)
            || routes[0]
            || this.state.currentPage
        this.openPagePreview(focused)
    }

    renderPreviewButton = (page, size = 'small', enabled = true) => (
        <Button
            type="link"
            size={size}
            icon={<EyeOutlined />}
            disabled={!enabled}
            title={enabled ? '打开页面' : '开通后可打开'}
            onClick={event => {
                event.preventDefault()
                event.stopPropagation()
                if (!enabled) return
                this.openPagePreview(page)
            }}
        >
            预览
        </Button>
    )

    renderCurrentPage = (currentPage, statusContext, role = 'apply') => {
        const page = resolveCurrentPage(currentPage, statusContext)
        if (!page) return null
        const currentRow = buildPageAccessList(currentPage, statusContext).find(row => row.current)
        // 申请页从被拦截链路进入时，当前页的拦截结果优先于状态接口里的旧权限数据。
        // 否则会出现标题写“没有权限”，当前页面却显示“已开通”的矛盾状态。
        const deniedByGateway = role === 'apply' && Boolean(this.deniedReason)
        const granted = role === 'grant' || (!deniedByGateway && currentRow?.status === 'granted')
        return <div className={`vibe-owner-access-current-page${role === 'grant' ? ' is-grant' : granted ? ' is-opened' : ' is-blocked'}`}>
            <div className="vibe-owner-access-current-page-icon">
                {granted && role !== 'grant' ? <CheckCircleOutlined /> : <ReadOutlined />}
            </div>
            <div className="vibe-owner-access-current-page-copy">
                <div className="vibe-owner-access-current-page-title">
                    <Text strong>{role === 'grant' ? '当前页面' : '无访问权限'}</Text>
                    {role === 'grant' && <Tag color="blue">可预览</Tag>}
                </div>
                <Text className="vibe-owner-access-current-page-name">{page.name}</Text>
                {role === 'grant'
                    ? (page.description ? <Text type="secondary">{page.description}</Text> : null)
                    : <Text className="vibe-owner-access-current-page-hint">项目负责人会收到钉钉通知。</Text>}
            </div>
            {role === 'grant' && this.renderPreviewButton(
                { ...page, url: page.url || currentPage?.url },
                'middle',
                true,
            )}
            {role === 'apply' && <div className="vibe-owner-access-current-page-action">
                {this.renderRequestButton(getDeniedReasonAction(this.deniedReason), false)}
            </div>}
        </div>
    }

    renderPageAccessList = (currentPage, statusContext) => {
        const rows = buildPageAccessList(currentPage, statusContext).filter(row => !row.current && row.status === 'granted')
        return <div className="vibe-owner-access-page-list">
            <div className="vibe-owner-access-section-heading">
                <div>
                    <Title level={5}>你已经能打开的页面</Title>
                    <Text type="secondary">这些页面不用再申请，可以直接打开。</Text>
                </div>
                <Tag color={rows.length ? 'green' : 'default'}>{rows.length ? `已开通 ${rows.length} 个` : '暂无其他页面'}</Tag>
            </div>
            {rows.length ? <div className="vibe-owner-access-page-list-body">{rows.map(row => (
                <div className="vibe-owner-access-page-row" key={row.key}>
                    <div className="vibe-owner-access-page-status">
                        <CheckCircleOutlined />
                    </div>
                    <div className="vibe-owner-access-page-copy">
                        <div className="vibe-owner-access-page-title">
                            <Text strong>{row.name}</Text>
                            <Tag color="green">已开通</Tag>
                        </div>
                        {row.description
                            ? <Text type="secondary">{row.description}</Text>
                            : <Text type="secondary">你已经可以打开这个页面。</Text>}
                    </div>
                    {this.renderPreviewButton(row, 'small', true)}
                </div>
                ))}</div> : <Empty image={Empty.PRESENTED_IMAGE_SIMPLE} description="暂无其他已开通页面。" />}
        </div>
    }

    requestAccess = async () => {
        if (!this.requestedProjectId || this.state.accessRequestSubmitting || this.state.accessRequested) return
        this.setState({ accessRequestSubmitting: true })
        try {
            const currentPage = resolveCurrentPage(this.state.currentPage, this.state.statusContext)
            await requestVibeOwnerPageAccess({
                projectId: this.requestedProjectId,
                returnPage: this.returnPage,
                pageName: currentPage?.name,
                pagePath: currentPage?.path,
            })
            this.setState({ accessRequestSubmitting: false, accessRequested: true })
            message.success('申请已发送')
        } catch (error) {
            this.setState({ accessRequestSubmitting: false })
            message.error(error?.message || '申请发送失败，请稍后重试')
        }
    }

    renderRequestButton = (actionContent, compact = false) => {
        const { accessRequestSubmitting, accessRequested } = this.state
        return <Tooltip title={accessRequested ? '钉钉消息已发送给项目负责人' : '点击后将钉钉消息发送给项目负责人，请求开通项目访问权限'}>
            <span>
                <Button
                    type="primary"
                    className="vibe-owner-access-primary-action"
                    size="middle"
                    loading={accessRequestSubmitting}
                    disabled={accessRequested}
                    onClick={this.requestAccess}
                >
                    {accessRequested ? actionContent.submittedLabel : actionContent.requestLabel}
                </Button>
            </span>
        </Tooltip>
    }

    loadProjects = async () => {
        this.setState({ loading: true, error: '' })
        try {
            const projects = await getVibeOwnerAccessProjects() || []
            const requested = projects.find(item => String(item.id) === String(this.requestedProjectId))
            const project = requested || projects[0]
            this.setState({ projects, projectId: project?.id, loading: false }, () => {
                if (project) this.loadContext(project.id)
            })
        } catch (error) {
            this.setState({ loading: false, error: error?.message || '应用列表加载失败，请稍后重试' })
        }
    }

    loadContext = async (projectId = this.state.projectId) => {
        if (!projectId) return
        this.setState({ loading: true, error: '', projectId })
        try {
            const context = await getVibeOwnerAccessContext({ projectId })
            const pageRoutes = (context?.routes || []).filter(route => route.routeType === 'PAGE')
            const focusedRoute = matchRouteByPage(pageRoutes, this.currentPage)
            this.setState({
                context,
                selectedRouteIds: pageRoutes.map(route => route.id),
                accessMode: undefined,
                subjectIds: [],
                drawerOpen: false,
                editingPermission: null,
                focusedRouteId: focusedRoute?.id || null,
                loading: false,
            })
        } catch (error) {
            this.setState({ loading: false, error: error?.message || '权限设置加载失败，请稍后重试' })
        }
    }

    loadOptions = async mode => {
        this.setState({ accessMode: mode, subjectIds: [] })
        if (mode === 'ALL' || (this.state.users.length && this.state.departments.length)) return
        this.setState({ optionLoading: true })
        try {
            const [users, departments] = await Promise.all([
                getUserListForDropbox({ type: 0 }), getDingdingDeptList({}),
            ])
            this.setState({ users: users || [], departments: departments || [] })
        } catch (error) {
            message.error(error?.message || '授权对象加载失败')
        } finally {
            this.setState({ optionLoading: false })
        }
    }

    copyPrompt = async () => {
        const prompt = this.state.context?.routeSyncPrompt || ''
        if (!prompt) {
            message.warning('暂时没有可复制的提示词')
            return
        }
        try {
            await navigator.clipboard.writeText(prompt)
            message.success('提示词已复制，请粘贴到对应项目的 Codex 聊天框执行')
        } catch (error) {
            message.error('复制失败，请检查浏览器剪贴板权限')
        }
    }

    deleteRoute = route => {
        const name = route.routeName || route.pathPattern || route.routeKey || '当前页面'
        Modal.confirm({
            title: `确认将“${name}”移出权限清单？`,
            content: '不会删除业务项目中的真实页面，只会移除上报页面并清理对应访问权限；后续可重新上报恢复。',
            okText: '移出权限清单',
            okButtonProps: { danger: true },
            cancelText: '取消',
            onOk: async () => {
                this.setState({ deletingRouteId: route.id })
                try {
                    await deleteVibePageAccessRoute({
                        projectId: this.state.projectId,
                        routeId: route.id,
                    })
                    message.success('已移出权限清单，相关权限已清理')
                    await this.loadContext(this.state.projectId)
                } catch (error) {
                    message.error(error?.message || '页面删除失败')
                    throw error
                } finally {
                    this.setState({ deletingRouteId: null })
                }
            },
        })
    }

    renderRouteSyncFooter = () => {
        const prompt = this.state.context?.routeSyncPrompt || ''
        return <div className="vibe-owner-access-sync-footer">
            <div className="vibe-owner-access-sync-heading">
                <div>
                    <Title level={5}>更新页面清单</Title>
                    <Text type="secondary">页面新增、移除或改名时，按下面三步更新：</Text>
                    <div className="vibe-owner-access-sync-steps">
                        <Text><b>1</b> 复制更新提示词</Text>
                        <Text><b>2</b> 粘贴到项目 Codex 执行</Text>
                        <Text><b>3</b> 点击“重新检测”</Text>
                    </div>
                </div>
                <Space size={8}>
                    <Tooltip title="查看给项目 Codex 执行的更新提示词">
                        <Button type="link" disabled={!prompt} onClick={() => this.setState(state => ({ routeSyncExpanded: !state.routeSyncExpanded }))}>
                            {this.state.routeSyncExpanded ? '收起提示词' : '查看提示词'}
                        </Button>
                    </Tooltip>
                    <Tooltip title="复制后粘贴到项目 Codex 执行">
                        <Button icon={<CopyOutlined />} disabled={!prompt} onClick={this.copyPrompt}>复制更新提示词</Button>
                    </Tooltip>
                    <Button icon={<ReloadOutlined />} onClick={() => this.loadContext()}>重新检测</Button>
                </Space>
            </div>
            {this.state.routeSyncExpanded && <div className="vibe-owner-access-sync-prompt-wrap">
                <pre className="vibe-owner-access-sync-prompt">{prompt || '暂时没有可复制的提示词'}</pre>
            </div>}
        </div>
    }

    renderReportPagesAlert = projectId => {
        const prompt = this.state.context?.routeSyncPrompt || ''
        return <>
            <Alert className="vibe-owner-access-alert" type="warning" showIcon
                message="还没有页面，先同步页面清单"
                description="复制提示词给项目 Codex 执行，完成后点击“重新检测”。"
                action={<Space direction="vertical" align="end">
                    <Space size={8}>
                        <Tooltip title="复制后，粘贴到对应项目的 Codex 聊天框执行">
                            <Button type="primary" icon={<CopyOutlined />} onClick={this.copyPrompt}>复制提示词</Button>
                        </Tooltip>
                        <Button type="link" disabled={!prompt} onClick={() => this.setState(state => ({ routeSyncExpanded: !state.routeSyncExpanded }))}>
                            {this.state.routeSyncExpanded ? '收起提示词' : '查看提示词'}
                        </Button>
                    </Space>
                    <Button icon={<ReloadOutlined />} onClick={() => this.loadContext(projectId)}>重新检测</Button>
                </Space>}
            />
            {this.state.routeSyncExpanded && <pre className="vibe-owner-access-sync-prompt vibe-owner-access-report-prompt">{prompt}</pre>}
        </>
    }

    openCreateDrawer = () => {
        const routes = (this.state.context?.routes || []).filter(route => route.routeType === 'PAGE')
        const focusedRoute = matchRouteByPage(routes, this.currentPage)
        const useFocusedOnly = this.deniedReason === 'PAGE_PERMISSION_NOT_GRANTED' && focusedRoute
        this.setState({
            drawerOpen: true, drawerMode: 'create', editingPermission: null,
            selectedRouteIds: useFocusedOnly ? [focusedRoute.id] : routes.map(route => route.id),
            accessMode: undefined, subjectIds: [], includeChildren: true,
            focusedRouteId: focusedRoute?.id || this.state.focusedRouteId,
        })
    }

    openEditDrawer = row => {
        this.setState({
            drawerOpen: true, drawerMode: 'edit', editingPermission: row,
            selectedRouteIds: row.routes.map(route => route.id), accessMode: row.subjectType,
            subjectIds: row.subjectId ? [String(row.subjectId)] : [], includeChildren: row.includeChildren !== false,
        })
    }

    closeDrawer = () => {
        if (!this.state.saving) this.setState({ drawerOpen: false, editingPermission: null })
    }

    savePermissionGroup = async () => {
        const { projectId, selectedRouteIds, accessMode, subjectIds, includeChildren } = this.state
        if (!accessMode) {
            message.warning('请先选择授权对象')
            return
        }
        if (!subjectIds.length && accessMode !== 'ALL') {
            message.warning('请选择同事或部门')
            return
        }
        this.setState({ saving: true })
        try {
            await replaceVibeOwnerPagePermission({
                projectId, routeIds: selectedRouteIds, subjectType: accessMode,
                subjectId: accessMode === 'ALL' ? '' : String(subjectIds[0]),
                subjectIds: accessMode === 'ALL' ? [] : subjectIds.map(String), includeChildren,
            })
            message.success(this.state.drawerMode === 'edit' ? '授权已更新' : '授权已添加')
            this.setState({ saving: false, drawerOpen: false, editingPermission: null }, () => this.loadContext())
        } catch (error) {
            this.setState({ saving: false })
            message.error(error?.message || '保存失败，请稍后重试')
        }
    }

    deletePermissionGroup = row => {
        const explicitCount = this.getPermissionRows(this.state.context,
            (this.state.context?.routes || []).filter(route => route.routeType === 'PAGE'))
            .filter(item => item.subjectType !== 'OWNER').length
        Modal.confirm({
            title: `取消“${row.subjectName}”的授权？`,
            content: <div><Paragraph>取消后，对方将不能再访问当前应用的 {row.routes.length} 个页面。</Paragraph>
                {explicitCount === 1 && <Alert type="warning" showIcon message="这是最后一条授权，取消后其他人将暂时无法访问这个应用。" />}</div>,
            okText: '确认取消授权', okButtonProps: { danger: true }, cancelText: '再想想',
            onOk: async () => {
                this.setState({ deletingKey: row.key })
                try {
                    await deleteVibeOwnerPagePermission({
                        projectId: this.state.projectId, subjectType: row.subjectType,
                        subjectId: row.subjectId || '', includeChildren: row.includeChildren === true,
                    })
                    message.success('授权已取消')
                    this.setState({ deletingKey: null }, () => this.loadContext())
                } catch (error) {
                    this.setState({ deletingKey: null })
                    message.error(error?.message || '删除失败，请稍后重试')
                    throw error
                }
            },
        })
    }

    getPermissionRows = (context, routes) => {
        if (!context) return []
        const grouped = new Map()
        routes.forEach(route => {
            ;(route.permissions || []).forEach(permission => {
                if (permission.subjectType === 'USER'
                    && String(permission.subjectId) === String(context.ownerUserId)) return
                const key = `${permission.subjectType}:${permission.subjectId || ''}`
                const existing = grouped.get(key) || {
                    key,
                    subjectType: permission.subjectType,
                    subjectId: permission.subjectId || '',
                    subjectName: permission.subjectName || permission.subjectId,
                    includeChildren: permission.includeChildren,
                    routes: [],
                    createdAt: permission.createdAt,
                }
                existing.routes.push(route)
                if (permission.createdAt && (!existing.createdAt || String(permission.createdAt) < String(existing.createdAt))) {
                    existing.createdAt = permission.createdAt
                }
                grouped.set(key, existing)
            })
        })
        return [{
            key: 'OWNER', subjectType: 'OWNER',
            subjectName: context.ownerUserNick || '项目创建人',
            routes, createdAt: context.createTime,
        }, ...Array.from(grouped.values())]
    }

    getApplicationEntryUrl = context => buildApplicationEntryUrl(context?.entryUrl)

    renderPermissionDrawer = routes => {
        const editing = this.state.drawerMode === 'edit'
        const typeName = { ALL: '全部员工', USER: '指定员工', DEPARTMENT: '指定部门' }
        const ownerUserId = String(this.state.context?.ownerUserId || '')
        const selectedIds = this.state.selectedRouteIds
        return <Modal
            title={editing ? '修改授权' : '新增授权'}
            width={760}
            visible={this.state.drawerOpen}
            onCancel={this.closeDrawer}
            destroyOnClose
            maskClosable={!this.state.saving}
            bodyStyle={{ maxHeight: 'calc(100vh - 220px)', overflowY: 'auto' }}
            confirmLoading={this.state.saving}
            okText={editing ? '保存修改' : '确认授权'}
            cancelText="取消"
            okButtonProps={{
                disabled: !selectedIds.length
                    || !this.state.accessMode
                    || (!this.state.subjectIds.length && this.state.accessMode !== 'ALL'),
            }}
            onOk={this.savePermissionGroup}
        >
            <div className="vibe-owner-access-drawer-intro">
                <Text>先选择谁可以访问，再勾选对方能打开的页面。</Text>
                <Text type="secondary">不确定页面是什么，可以先预览再保存。</Text>
            </div>
            <div className="vibe-owner-access-grant">
                <Title level={5}>1. 选择授权对象</Title>
                {editing ? <div className="vibe-owner-access-locked-subject"><Text strong>{this.state.editingPermission?.subjectName}</Text>
                    <Tag>{typeName[this.state.accessMode]}</Tag><Text type="secondary">如需更换对象，请取消后重新授权。</Text></div>
                    : <Radio.Group value={this.state.accessMode} onChange={event => this.loadOptions(event.target.value)}>
                        <Radio value="USER">指定员工</Radio><Radio value="DEPARTMENT">指定部门</Radio><Radio value="ALL">全部员工</Radio>
                    </Radio.Group>}
                {!editing && this.state.accessMode === 'USER' && <Select showSearch mode="multiple" maxTagCount="responsive" placeholder="选择同事（可多选）" value={this.state.subjectIds} onChange={subjectIds => this.setState({ subjectIds })}
                    loading={this.state.optionLoading}
                    notFoundContent={this.state.optionLoading ? '正在加载员工…' : '没有可选择的员工'}
                    optionFilterProp="label"
                    options={buildUserOptions(this.state.users)
                        .filter(option => String(option.value) !== ownerUserId)} />}
                {this.state.accessMode === 'DEPARTMENT' && <Space>{!editing && <Select showSearch mode="multiple" maxTagCount="responsive" placeholder="选择部门（可多选）" value={this.state.subjectIds} onChange={subjectIds => this.setState({ subjectIds })}
                    loading={this.state.optionLoading}
                    notFoundContent={this.state.optionLoading ? '正在加载部门…' : '没有可选择的部门'}
                    optionFilterProp="label" options={buildDepartmentOptions(this.state.departments)} />}
                    <Checkbox checked={this.state.includeChildren} onChange={event => this.setState({ includeChildren: event.target.checked })}>包含下级部门</Checkbox></Space>}
                <Paragraph type="secondary">项目创建人始终可以打开全部页面，不用再给自己授权。</Paragraph>
            </div>
            <div className="vibe-owner-access-routes">
                <div className="vibe-owner-access-section-heading">
                    <div>
                        <Title level={5}>2. 选择可访问页面</Title>
                        <Text type="secondary">已选 {selectedIds.length} / {routes.length} 个页面</Text>
                    </div>
                </div>
                <VibeRouteList
                    routes={routes}
                    selectedIds={selectedIds}
                    focusedRouteId={this.state.focusedRouteId}
                    onSelectionChange={selectedRouteIds => this.setState({ selectedRouteIds })}
                    onPreview={this.renderPreviewButton}
                    onDelete={this.deleteRoute}
                />
            </div>
        </Modal>
    }

    renderGrantedPagesPopover = routeList => (
        <div className="vibe-owner-access-page-popover">
            {(routeList || []).map(route => (
                <div className="vibe-owner-access-page-popover-item" key={route.id || route.pathPattern}>
                    <div>
                        <Text strong>{route.routeName || route.name}</Text>
                        {route.description ? <Text type="secondary">{route.description}</Text> : null}
                    </div>
                    {this.renderPreviewButton(route)}
                </div>
            ))}
        </div>
    )

    renderGrantedPagesSummary = (routeList, allRoutes) => {
        const summary = summarizeGrantedPages(routeList, allRoutes)
        if (!summary.count) return <Text type="secondary">{summary.label}</Text>
        return <Popover
            trigger={['hover', 'click']}
            placement="bottomLeft"
            overlayClassName="vibe-owner-access-page-popover-overlay"
            content={this.renderGrantedPagesPopover(routeList)}
        >
            <button type="button" className={`vibe-owner-access-page-summary${summary.all ? ' is-all' : ''}`}>
                {summary.label}
            </button>
        </Popover>
    }

    renderPermissionList = (context, routes) => {
        const rows = this.getPermissionRows(context, routes)
        const applicationEntryUrl = this.getApplicationEntryUrl(context)
        const setupState = getOwnerSetupState(context)
        const focusedRoute = matchRouteByPage(routes, this.state.currentPage) || context.currentRoute
        const currentPage = this.state.currentPage || (focusedRoute ? {
            name: focusedRoute.routeName || focusedRoute.routeKey || '当前页面',
            path: focusedRoute.pathPattern || focusedRoute.path || '/',
            description: focusedRoute.description,
        } : null)
        const typeMeta = {
            OWNER: { color: 'gold', text: '项目创建人' },
            USER: { color: 'blue', text: '员工' },
            DEPARTMENT: { color: 'purple', text: '部门' },
            ALL: { color: 'green', text: '全部员工' },
        }
        const columns = [{
            title: '谁可以访问', dataIndex: 'subjectName', width: 220,
            render: (value, row) => <div className="vibe-owner-access-subject"><Text strong>{value}</Text>
                <Tag color={typeMeta[row.subjectType]?.color}>{typeMeta[row.subjectType]?.text || row.subjectType}</Tag>
                {row.subjectType === 'DEPARTMENT' && row.includeChildren ? <Text type="secondary">包含下级部门</Text> : null}</div>,
        }, {
            title: '已设置页面', dataIndex: 'routes',
            render: routeList => this.renderGrantedPagesSummary(routeList, routes),
        }, {
            title: '设置时间', dataIndex: 'createdAt', width: 170,
            render: value => <Text type="secondary">{formatTime(value)}</Text>,
        }, {
            title: '操作', key: 'action', width: 150, fixed: 'right',
            render: (_, row) => row.subjectType === 'OWNER' ? null : <Space>
                <Button type="link" size="small" icon={<EditOutlined />} onClick={() => this.openEditDrawer(row)}>修改</Button>
                <Button type="link" size="small" danger icon={<DeleteOutlined />} loading={this.state.deletingKey === row.key}
                    onClick={() => this.deletePermissionGroup(row)}>取消授权</Button>
            </Space>,
        }]
        const setupNotice = setupState === 'GRANT_ACCESS' && <Alert className="vibe-owner-access-initial-alert" type="warning" showIcon
                message={<span className="vibe-owner-access-initial-alert-title">尚未分配权限</span>}
                action={<Button className="vibe-owner-access-primary-action" type="primary" size="middle" icon={<UserAddOutlined />} onClick={this.openCreateDrawer}>分配权限</Button>} />
        return <VibeOwnerAccessPermissionPanel
            setupState={setupState}
            setupNotice={setupNotice}
            currentPage={currentPage}
            currentPageView={this.renderCurrentPage(currentPage, { currentRoute: focusedRoute }, 'grant')}
            applicationEntryUrl={applicationEntryUrl}
            onOpenApplication={() => window.location.assign(applicationEntryUrl)}
            onCreatePermission={this.openCreateDrawer}
            onRenderRouteSync={() => this.renderRouteSyncFooter()}
        >
            <Table rowKey="key" columns={columns} dataSource={rows} pagination={false} scroll={{ x: 760 }} />
        </VibeOwnerAccessPermissionPanel>
    }

    renderStatus = () => {
        const { loading, error, statusContext, accessRequestSubmitting, accessRequested, currentPage } = this.state
        const retryUrl = resolveSafeReturnPage(this.returnPage, statusContext?.entryUrl)
            || statusContext?.entryUrl
        return (
        <div className="vibe-owner-access-page"><Card className="vibe-owner-access-status">
            <Spin spinning={loading}>
                {error ? <Alert type="error" showIcon message={error} /> : <>
                    {this.renderCurrentPage(currentPage, statusContext, 'apply')}
                    {this.renderPageAccessList(currentPage, statusContext)}
                </>}
                {accessRequested && <Alert className="vibe-owner-access-request-success" type="success" showIcon
                    icon={<CheckCircleOutlined />} message="钉钉消息已发送"
                    description="项目负责人处理后，点击“重新打开页面”。" />}
                <Space className="vibe-owner-access-status-actions" wrap>
                    <Button type="primary" ghost disabled={!retryUrl} onClick={() => window.location.assign(retryUrl)}>重新打开页面</Button>
                    <Button type="link" href="/portal/dashboard">返回工作台</Button>
                    {error && <Button onClick={this.loadStatusContext}>重试加载</Button>}
                </Space>
            </Spin>
        </Card></div>
        )
    }

    render() {
        const { loading, error, projects, projectId, context } = this.state
        const routes = (context?.routes || []).filter(route => route.routeType === 'PAGE')
        const setupState = getOwnerSetupState(context || {})
        const setupSteps = [{ title: '上报页面' }, { title: '分配权限' }]
        if (this.statusView) return this.renderStatus()
        if (this.props.embedded) {
            return <div className="vibe-owner-access-embedded">
                <Spin spinning={loading}>
                    <div className="vibe-owner-access-embedded-intro">
                        <div>
                            <Text type="secondary">页面授权</Text>
                            <Text strong>{context?.projectName || '当前应用'}</Text>
                        <Text type="secondary">设置谁可以访问哪些页面。</Text>
                        </div>
                        <Tag color={setupState === 'COMPLETE' ? 'green' : 'gold'}>
                            {setupState === 'REPORT_PAGES' ? '未上报页面' : setupState === 'GRANT_ACCESS' ? '未分配权限' : '已分配权限'}
                        </Tag>
                    </div>
                    {error && <Alert type="error" showIcon message={error}
                        action={<Button onClick={() => this.loadContext(projectId)}>重试</Button>} />}
                    {!error && !loading && !context && <Empty description="暂时无法获取应用权限信息" />}
                    {!error && context && setupState === 'REPORT_PAGES' && <Steps current={0} items={setupSteps} />}
                    {!error && context && setupState === 'REPORT_PAGES' && <>
                            {this.renderReportPagesAlert(projectId)}
                    </>}
                    {!!routes.length && this.renderPermissionList(context, routes)}
                    {!!routes.length && this.renderPermissionDrawer(routes)}
                </Spin>
            </div>
        }
        return <div className="vibe-owner-access-page">
            <div className="vibe-owner-access-heading">
                <div className="vibe-owner-access-heading-icon"><SafetyCertificateOutlined /></div>
                <div>
                    <div className="vibe-owner-access-heading-title">
                        <Title level={3}>页面授权</Title>
                    </div>
                    <Text type="secondary">给同事开通页面访问权限。</Text>
                </div>
            </div>
            <Card bordered={false} className="vibe-owner-access-card">
                <Spin spinning={loading}>
                    {error && <Alert type="error" showIcon message={error} action={<Button onClick={this.loadProjects}>重试</Button>} />}
                    {!error && !loading && !projects.length && <Empty description="你还没有可配置的内部应用" />}
                    {!!projects.length && <>
                        {setupState === 'REPORT_PAGES' && <Steps className="vibe-owner-access-steps" current={0} items={setupSteps} />}
                        {context && setupState === 'REPORT_PAGES' && <>
                            {this.renderReportPagesAlert()}
                        </>}
                        {!!routes.length && this.renderPermissionList(context, routes)}
                        {!!routes.length && this.renderPermissionDrawer(routes)}
                    </>}
                </Spin>
            </Card>
        </div>
    }
}
